import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Trophy, ArrowRight, Home, MapPin, Calendar, Building2, ExternalLink, PlayCircle, X, ChevronLeft, ChevronRight, Images, Search, ArrowDownAZ, ArrowUpAZ, CalendarDays } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const KIND_META = {
  course: { label: "دورة", emoji: "📚", cls: "bg-violet-100 text-violet-800 border-violet-300" },
  workshop: { label: "ورشة", emoji: "🛠️", cls: "bg-fuchsia-100 text-fuchsia-800 border-fuchsia-300" },
  lecture: { label: "محاضرة", emoji: "🎤", cls: "bg-pink-100 text-pink-800 border-pink-300" },
  talk: { label: "حديث", emoji: "🗣️", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  seminar: { label: "ندوة", emoji: "🎓", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
};

export default function ContributionsPage() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest"); // newest | oldest | alpha
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // {cid, mediaList, index}

  useEffect(() => {
    document.title = "الإسهامات الخارجية للمدير — دورات وورش عمل | مركز Happy Day";
    // Add meta description dynamically for SEO
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = "قائمة الدورات التدريبية وورش العمل والمحاضرات التي قدّمها مدير مركز Happy Day في مؤسسات وجمعيات مختلفة — تربية خاصة، ABA، IEP.";

    axios.get(`${BACKEND}/api/public/director-contributions`)
      .then((r) => setItems(r.data || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, []);

  const counts = items.reduce((acc, c) => { acc[c.kind] = (acc[c.kind] || 0) + 1; return acc; }, {});

  const sortedFiltered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const dateKey = (c) => {
      if (c.event_date) return new Date(c.event_date).getTime();
      if (c.year) return new Date(`${c.year}-01-01`).getTime();
      if (c.created_at) return new Date(c.created_at).getTime();
      return 0;
    };
    let out = filter === "all" ? items.slice() : items.filter((c) => c.kind === filter);
    if (q) {
      out = out.filter((c) => {
        const blob = [c.title, c.description, c.org, c.location].filter(Boolean).join(" ").toLowerCase();
        return blob.includes(q);
      });
    }
    out.sort((a, b) => {
      if (sort === "alpha") return (a.title || "").localeCompare(b.title || "", "ar");
      const da = dateKey(a), dbb = dateKey(b);
      return sort === "oldest" ? da - dbb : dbb - da;
    });
    return out;
  }, [items, filter, query, sort]);

  return (
    <div dir="rtl" className="min-h-screen bg-gradient-to-br from-violet-50 via-white to-fuchsia-50" data-testid="contributions-page">
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-l from-violet-600 via-fuchsia-600 to-pink-600 opacity-95" />
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-80 h-80 rounded-full bg-white/10 blur-3xl" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-14 lg:py-20 text-white">
          <Link to="/welcome" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-5 text-sm font-bold" data-testid="back-to-landing">
            <Home className="w-4 h-4" /> الرئيسية <ArrowRight className="w-3 h-3" />
          </Link>
          <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur rounded-full px-4 py-1.5 text-xs font-black tracking-widest mb-4">
            <Trophy className="w-4 h-4" /> إسهامات المدير الخارجية
          </div>
          <h1 className="text-3xl md:text-5xl font-black leading-tight" data-testid="contributions-h1">
            دوراتي التدريبية وورش العمل
          </h1>
          <p className="mt-4 text-white/90 text-lg max-w-2xl leading-relaxed">
            كل الدورات وورش العمل والمحاضرات التي قدّمتها في مؤسسات وجمعيات خارج مركز Happy Day — تربية خاصة، تحليل السلوك التطبيقي، والخطط التربوية الفردية.
          </p>
        </div>
      </div>

      {/* Filters + Grid */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-14">
        {items.length > 0 && (
          <div className="mb-6 space-y-4" data-testid="contribs-toolbar">
            {/* Search + Sort row */}
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
              <div className="relative flex-1">
                <Search className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="ابحث بالعنوان، الجهة، المكان، أو الوصف..."
                  className="w-full pr-10 pl-10 py-2.5 rounded-full bg-white border-2 border-violet-100 focus:border-violet-400 focus:ring-2 focus:ring-violet-100 outline-none text-sm font-medium"
                  data-testid="contribs-search"
                />
                {query && (
                  <button onClick={() => setQuery("")} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-rose-600" data-testid="contribs-search-clear" title="مسح">
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2 bg-white rounded-full border-2 border-violet-100 px-1 py-1 shrink-0" data-testid="contribs-sort">
                {[
                  { id: "newest", label: "الأحدث", Icon: CalendarDays },
                  { id: "oldest", label: "الأقدم", Icon: Calendar },
                  { id: "alpha", label: "أ→ي", Icon: ArrowDownAZ },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => setSort(s.id)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-black transition ${sort === s.id ? "bg-violet-600 text-white shadow" : "text-violet-700 hover:bg-violet-50"}`}
                    data-testid={`contribs-sort-${s.id}`}
                  >
                    <s.Icon className="w-3.5 h-3.5" /> {s.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Kind filter row */}
            <div className="flex items-center gap-2 flex-wrap" data-testid="contribs-filters">
              {[
                { id: "all", label: "الكل", count: items.length },
                { id: "course", label: "📚 دورات", count: counts.course || 0 },
                { id: "workshop", label: "🛠️ ورش", count: counts.workshop || 0 },
                { id: "lecture", label: "🎤 محاضرات", count: counts.lecture || 0 },
                { id: "seminar", label: "🎓 ندوات", count: counts.seminar || 0 },
                { id: "talk", label: "🗣️ أحاديث", count: counts.talk || 0 },
              ].filter((f) => f.count > 0 || f.id === "all").map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFilter(f.id)}
                  className={`px-4 py-2 rounded-full text-sm font-black transition ${filter === f.id ? "bg-violet-600 text-white shadow" : "bg-white border border-violet-200 text-violet-700 hover:bg-violet-50"}`}
                  data-testid={`contribs-filter-${f.id}`}
                >
                  {f.label} <span className="opacity-70">({f.count})</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center py-20 text-slate-400">جارٍ التحميل...</div>
        ) : items.length === 0 ? (
          <div className="text-center py-20">
            <Trophy className="w-16 h-16 mx-auto text-slate-300 mb-3" />
            <div className="text-slate-500 font-bold text-lg">لم تُضَف إسهامات بعد</div>
            <div className="text-sm text-slate-400 mt-1">سيتم رفع الدورات وورش العمل هنا قريباً</div>
          </div>
        ) : sortedFiltered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-3xl border-2 border-violet-100">
            <Search className="w-14 h-14 mx-auto text-violet-300 mb-3" />
            <div className="text-slate-700 font-black text-lg">لا نتائج مطابقة</div>
            <div className="text-sm text-slate-500 mt-1">جرّب تغيير الكلمة المفتاحية أو إزالة الفلتر</div>
            <button
              onClick={() => { setQuery(""); setFilter("all"); }}
              className="mt-4 px-5 py-2 rounded-full bg-violet-600 hover:bg-violet-700 text-white font-black text-xs"
              data-testid="contribs-reset"
            >
              إعادة تعيين البحث
            </button>
          </div>
        ) : (
          <>
            <div className="mb-3 text-xs font-bold text-slate-500" data-testid="contribs-count">
              {sortedFiltered.length} من {items.length} إسهامة
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="contribs-grid">
              {sortedFiltered.map((c) => {
                const meta = KIND_META[c.kind] || KIND_META.course;
                return (
                <article key={c.id} className="rounded-3xl bg-white border-2 border-violet-100 shadow-sm hover:shadow-xl hover:border-violet-300 transition-all overflow-hidden group" data-testid={`contrib-card-${c.id}`}>
                  <MediaHeader contribution={c} onOpenLightbox={setLightbox} />
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[11px] font-black border ${meta.cls}`}>
                        {meta.emoji} {meta.label}
                      </span>
                      {c.year && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-black text-slate-500 bg-slate-100 px-2 py-0.5 rounded-full">
                          <Calendar className="w-3 h-3" /> {c.year}
                        </span>
                      )}
                    </div>
                    <h2 className="font-black text-slate-900 text-lg leading-snug" data-testid={`contrib-title-${c.id}`}>{c.title}</h2>
                    {c.event_date && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-emerald-700 font-black bg-emerald-50 border border-emerald-200 rounded-full px-2.5 py-1 inline-flex w-fit">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(c.event_date).toLocaleDateString("ar-EG", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
                      </div>
                    )}
                    {c.org && (
                      <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-600 font-bold">
                        <Building2 className="w-3.5 h-3.5 text-violet-500 shrink-0" />
                        <span className="truncate">{c.org}</span>
                      </div>
                    )}
                    {c.location && (
                      <div className="flex items-center gap-1.5 mt-1 text-xs text-slate-500 font-bold">
                        <MapPin className="w-3.5 h-3.5 text-fuchsia-500 shrink-0" />
                        <span className="truncate">{c.location}</span>
                      </div>
                    )}
                    {c.description && <p className="mt-3 text-sm text-slate-700 leading-relaxed whitespace-pre-line">{c.description}</p>}
                    {(c.testimonials || []).length > 0 && (
                      <div className="mt-4 rounded-2xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-white p-3 space-y-2.5" data-testid={`contrib-testimonials-${c.id}`}>
                        <div className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-wider text-amber-800">
                          <span className="text-base">💬</span> ماذا قالوا عن الندوة ({c.testimonials.length})
                        </div>
                        {c.testimonials.map((t) => (
                          <blockquote key={t.id} className="relative rounded-xl bg-white border border-amber-100 px-3 py-2.5 shadow-sm" data-testid={`contrib-testimonial-${t.id}`}>
                            <div className="absolute top-1 right-2 text-3xl leading-none text-amber-300 font-serif select-none">"</div>
                            <p className="text-xs text-slate-700 leading-relaxed pr-4 italic">{t.quote}</p>
                            <div className="mt-1.5 flex items-center gap-1.5 text-[10px] font-black">
                              <span className="text-amber-900">— {t.name}</span>
                              {t.role && <span className="text-slate-500 font-bold">· {t.role}</span>}
                            </div>
                          </blockquote>
                        ))}
                      </div>
                    )}
                    {c.url && (
                      <a href={c.url} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-1 text-xs font-black text-violet-700 hover:underline" data-testid={`contrib-external-${c.id}`}>
                        <ExternalLink className="w-3.5 h-3.5" /> معلومات إضافية
                      </a>
                    )}
                    {(c.media || []).length > 0 && (
                      <button
                        onClick={() => setLightbox({ cid: c.id, mediaList: c.media, index: 0, title: c.title })}
                        className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl bg-gradient-to-l from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 text-white font-black text-sm shadow-md transition"
                        data-testid={`contrib-gallery-${c.id}`}
                      >
                        <Images className="w-4 h-4" /> عرض الألبوم ({c.media.length})
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
            </div>
          </>
        )}

        <div className="mt-14 text-center">
          <Link to="/welcome" className="inline-flex items-center gap-2 px-6 py-3 rounded-2xl bg-slate-900 hover:bg-slate-800 text-white font-black text-sm" data-testid="back-to-home">
            <Home className="w-4 h-4" /> العودة للرئيسية
          </Link>
        </div>
      </div>
      {lightbox && <MediaLightbox {...lightbox} onClose={() => setLightbox(null)} onIndex={(i) => setLightbox((s) => s && { ...s, index: i })} />}
    </div>
  );
}

function mediaUrl(cid, mid) {
  return `${BACKEND}/api/public/contribution-media/${cid}/${mid}`;
}

function MediaHeader({ contribution: c, onOpenLightbox }) {
  const media = c.media || [];
  if (media.length === 0 && !c.image_url) return null;

  if (media.length === 0 && c.image_url) {
    return (
      <div className="aspect-[16/9] overflow-hidden bg-slate-100">
        <img src={c.image_url} alt={c.title} loading="lazy" className="w-full h-full object-cover" />
      </div>
    );
  }

  // Cover priority: pinned cover_media_id > video > first item
  let hero;
  if (c.cover_media_id) {
    hero = media.find((m) => m.id === c.cover_media_id);
  }
  if (!hero) hero = media.find((m) => m.type === "video") || media[0];
  const others = media.filter((m) => m.id !== hero.id).slice(0, 3);
  const extraCount = Math.max(media.length - 1 - others.length, 0);
  const heroIndex = media.findIndex((m) => m.id === hero.id);

  return (
    <div className="bg-slate-900 relative">
      <button
        type="button"
        onClick={() => onOpenLightbox({ cid: c.id, mediaList: media, index: heroIndex, title: c.title })}
        className="block w-full aspect-[16/9] relative group overflow-hidden"
        data-testid={`contrib-hero-${c.id}`}
      >
        {hero.type === "video" ? (
          <>
            <video
              src={mediaUrl(c.id, hero.id)}
              poster={hero.poster_path ? `${mediaUrl(c.id, hero.id)}/poster` : undefined}
              muted
              loop
              autoPlay
              playsInline
              preload="auto"
              className="w-full h-full object-cover"
              data-testid={`contrib-hero-video-${c.id}`}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/25 to-black/10 flex items-center justify-center group-hover:from-black/85 transition">
              <span className="relative flex items-center justify-center">
                <span className="absolute w-28 h-28 rounded-full bg-white/25 animate-ping" />
                <span className="absolute w-24 h-24 rounded-full bg-white/20" />
                <span className="relative w-24 h-24 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                  <PlayCircle className="w-14 h-14 text-violet-700 fill-violet-100" />
                </span>
              </span>
              <span className="absolute top-3 right-3 bg-red-600 text-white text-[10px] font-black px-2.5 py-1 rounded-full flex items-center gap-1 shadow-lg" data-testid={`contrib-video-badge-${c.id}`}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" /> فيديو
              </span>
              {hero.caption && (
                <span className="absolute bottom-3 left-3 right-3 text-white text-xs md:text-sm font-bold drop-shadow-lg line-clamp-2 text-center">
                  {hero.caption}
                </span>
              )}
            </div>
          </>
        ) : (
          <img src={mediaUrl(c.id, hero.id)} alt={c.title} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        )}
      </button>
      {(others.length > 0 || extraCount > 0) && (
        <div className="grid grid-cols-4 gap-0.5">
          {others.map((m, i) => (
            <button
              key={m.id}
              onClick={() => onOpenLightbox({ cid: c.id, mediaList: media, index: media.findIndex((x) => x.id === m.id), title: c.title })}
              className="aspect-square bg-slate-800 relative overflow-hidden hover:opacity-80 transition"
              data-testid={`contrib-thumb-${c.id}-${i}`}
            >
              {m.type === "video" ? (
                <>
                  <video src={mediaUrl(c.id, m.id)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                    <PlayCircle className="w-6 h-6 text-white" />
                  </div>
                </>
              ) : (
                <img src={mediaUrl(c.id, m.id)} alt="" loading="lazy" className="w-full h-full object-cover" />
              )}
            </button>
          ))}
          {extraCount > 0 && (
            <button
              onClick={() => onOpenLightbox({ cid: c.id, mediaList: media, index: 0, title: c.title })}
              className="aspect-square bg-slate-800/95 text-white font-black text-lg flex items-center justify-center hover:bg-slate-700 transition"
              data-testid={`contrib-more-${c.id}`}
            >
              +{extraCount}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function MediaLightbox({ cid, mediaList, index, title, onClose, onIndex }) {
  const m = mediaList[index];
  const prev = () => onIndex((index - 1 + mediaList.length) % mediaList.length);
  const next = () => onIndex((index + 1) % mediaList.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") next();
      if (e.key === "ArrowRight") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  if (!m) return null;
  return (
    <div className="fixed inset-0 z-[200] bg-black/95 backdrop-blur flex flex-col" data-testid="contrib-lightbox">
      <div className="flex items-center justify-between p-4 text-white">
        <div className="min-w-0">
          <div className="text-xs opacity-70">{index + 1} / {mediaList.length}</div>
          <div className="font-black text-lg truncate max-w-sm">{title}</div>
        </div>
        <button onClick={onClose} className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center" data-testid="contrib-lightbox-close">
          <X className="w-6 h-6" />
        </button>
      </div>
      <div className="flex-1 relative flex items-center justify-center px-2 md:px-16">
        {mediaList.length > 1 && (
          <>
            <button onClick={prev} className="absolute right-2 md:right-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10" data-testid="contrib-lightbox-prev">
              <ChevronRight className="w-7 h-7" />
            </button>
            <button onClick={next} className="absolute left-2 md:left-6 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center z-10" data-testid="contrib-lightbox-next">
              <ChevronLeft className="w-7 h-7" />
            </button>
          </>
        )}
        <div className="w-full h-full flex items-center justify-center flex-col">
          {m.type === "video" ? (
            <video key={m.id} src={mediaUrl(cid, m.id)} controls autoPlay playsInline className="max-w-full max-h-[70vh] rounded-xl shadow-2xl" />
          ) : (
            <img key={m.id} src={mediaUrl(cid, m.id)} alt="" className="max-w-full max-h-[70vh] rounded-xl shadow-2xl object-contain" />
          )}
          {m.caption && (
            <div className="mt-4 max-w-3xl w-full mx-auto px-4">
              <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl px-5 py-3 text-white text-center" data-testid="contrib-lightbox-caption">
                <p className="text-sm md:text-base font-medium leading-relaxed whitespace-pre-line">{m.caption}</p>
              </div>
            </div>
          )}
        </div>
      </div>
      {mediaList.length > 1 && (
        <div className="p-3 overflow-x-auto">
          <div className="flex gap-2 justify-center min-w-max px-2">
            {mediaList.map((mm, i) => (
              <button
                key={mm.id}
                onClick={() => onIndex(i)}
                className={`w-16 h-16 shrink-0 rounded-lg overflow-hidden border-2 transition ${i === index ? "border-white" : "border-transparent opacity-60 hover:opacity-100"}`}
                data-testid={`contrib-lightbox-thumb-${i}`}
              >
                {mm.type === "video" ? (
                  <div className="w-full h-full bg-slate-800 flex items-center justify-center relative">
                    <video src={mediaUrl(cid, mm.id)} muted playsInline preload="metadata" className="w-full h-full object-cover" />
                    <PlayCircle className="w-5 h-5 text-white absolute" />
                  </div>
                ) : (
                  <img src={mediaUrl(cid, mm.id)} alt="" className="w-full h-full object-cover" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
